
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserSpgEntity from 'Models/Security/Acl/UserSpgEntity';
import VisitorsSpgEntity from 'Models/Security/Acl/VisitorsSpgEntity';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';

import type { Errors } from '../../Util/CustomTypes';
import { isPhone } from '../../Validators/Functions/Custom';
import { isEmail } from '../../Validators/Functions/Email';
import SpgRegistrationProfileEntity, { ISpgRegistrationProfileEntityAttributes } from './SpgRegistrationProfileEntity';

export interface ISpgEntityAttributes extends IModelAttributes {
	contactName: string;
	organisationalNumbers: string;
	spgNumber: string;
	phone: string;
	accessCode: string;
	isActive: boolean;
	legacyID: string;
	email: string;
	spgName: string;

	businessEntityId?: string;
	businessEntity?: Models.BusinessEntity | Models.IBusinessEntityAttributes;

	spgRegistrationProfiles?: SpgRegistrationProfileEntity[] | ISpgRegistrationProfileEntityAttributes[];
}

@entity('SpgEntity', 'SPG')
export default class SpgEntity extends Model
	implements ISpgEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserSpgEntity(),
		new VisitorsSpgEntity(),
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<SpgEntity, string>()
	@CRUD({
		name: 'Contact Name',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public contactName: string;
	
	/**
	 * Comma seperated string of organisation numbers
	 */
	@observable
	@attribute<SpgEntity, string>()
	@CRUD({
		name: 'Organisational Numbers',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public organisationalNumbers: string;

	@observable
	@attribute<SpgEntity, string>()
	@CRUD({
		name: 'SPG Number',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public spgNumber: string;

	@observable
	@attribute<SpgEntity, string>()
	@CRUD({
		name: 'Phone',
		displayType: 'textfield',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public phone: string;

	@observable
	@attribute<SpgEntity, string>()
	@CRUD({
		name: 'Access Code',
		displayType: 'textfield',
		order: 50,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public accessCode: string;

	@observable
	@attribute<SpgEntity, boolean>()
	@CRUD({
		name: 'Is Active',
		displayType: 'checkbox',
		order: 60,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public isActive: boolean;
	

	
	@Validators.Unique()
	@observable
	@attribute<SpgEntity, string>()
	@CRUD({
		name: 'Legacy ID',
		displayType: 'textfield',
		order: 70,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public legacyID: string;

	@observable
	@attribute<SpgEntity, string>()
	@CRUD({
		name: 'Email',
		displayType: 'textfield',
		order: 80,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public email: string;

	@observable
	@attribute<SpgEntity, string>()
	@CRUD({
		name: 'SPG Name',
		displayType: 'textfield',
		order: 90,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public spgName: string;

	/**
	 * PPSR SPGs
	 */
	@observable
	@attribute()
	@CRUD({

		name: 'Business Entity',
		displayType: 'reference-combobox',
		order: 100,
		referenceTypeFunc: () => Models.BusinessEntity,

	})
	public businessEntityId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public businessEntity: Models.BusinessEntity;

	@observable
	public spgRegistrationProfiles: SpgRegistrationProfileEntity[] = [];

	@action public validateField = async (field: string) => {
		switch (field) {
			case 'phone': {
				this.setFieldError('phone', !!this.phone && !isPhone(this.phone), 'Phone number must be valid');
				break;
			}
			case 'email': {
				this.setFieldError('email', !!this.email && !isEmail(this.email), 'Email address must be valid');
				break;
			}
		}
	}

	public validateAllFields = async () => {
		const fieldList = ['phone', 'email'];
		const validationPromises = fieldList.map(field => this.validateField(field));
		await Promise.all(validationPromises);
	}

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<ISpgEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<ISpgEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.contactName !== undefined) {
				this.contactName = attributes.contactName;
			}
			if (attributes.organisationalNumbers !== undefined) {
				this.organisationalNumbers = attributes.organisationalNumbers;
			}
			if (attributes.spgNumber !== undefined) {
				this.spgNumber = attributes.spgNumber;
			}
			if (attributes.phone !== undefined) {
				this.phone = attributes.phone;
			}
			if (attributes.accessCode !== undefined) {
				this.accessCode = attributes.accessCode;
			}
			if (attributes.isActive !== undefined) {
				this.isActive = attributes.isActive;
			}
			if (attributes.legacyID !== undefined) {
				this.legacyID = attributes.legacyID;
			}
			if (attributes.email !== undefined) {
				this.email = attributes.email;
			}
			if (attributes.spgName !== undefined) {
				this.spgName = attributes.spgName;
			}
			if (attributes.businessEntityId !== undefined) {
				this.businessEntityId = attributes.businessEntityId;
			}
			if (attributes.businessEntity !== undefined) {
				if (attributes.businessEntity === null) {
					this.businessEntity = attributes.businessEntity;
				} else if (attributes.businessEntity instanceof Models.BusinessEntity) {
					this.businessEntity = attributes.businessEntity;
					this.businessEntityId = attributes.businessEntity.id;
				} else {
					this.businessEntity = new Models.BusinessEntity(attributes.businessEntity);
					this.businessEntityId = this.businessEntity.id;
				}
			}
			if (attributes.spgRegistrationProfiles !== undefined) {
				this.spgRegistrationProfiles = (attributes.spgRegistrationProfiles ?? []).map(profile =>
					profile instanceof SpgRegistrationProfileEntity
						? profile
						: new SpgRegistrationProfileEntity(profile),
				);
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		businessEntity {
			${Models.BusinessEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}

	public validateDetailsFields = async () => {
		const fieldList = [
			'phone', 'email',
		];
		const validationPromises = fieldList.map(field => this.validateField(field));
		await Promise.all(validationPromises);
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(SpgEntity.prototype, 'created');
CRUD(modifiedAttr)(SpgEntity.prototype, 'modified');
